import {
  ensureProjectFolder,
  shareFileWithLink,
  uploadFileToDrive,
  withDriveToken,
} from '../files/drive';
import { orderedProofs, reviewUrl } from './clientLink';
import type { ClientLinkDraft } from './clientLink';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * Turning a filled-in wizard into a live /review/[token] URL.
 *
 * Three writes, in an order the database will not let us change, and each one
 * harder to undo than the last:
 *
 *   1. the cg_order row      — order_assets.cg_order_id is NOT NULL
 *   2. one asset per slot    — upload_order_asset numbers versions from what
 *                              already exists for that component, so v1 is a
 *                              consequence of going first, not a value we set
 *   3. the review token      — LAST, and this is the load-bearing part
 *
 * ── Why the token is minted last ───────────────────────────────────────────
 *
 * A token is a public URL. Minting it before the proofs landed produces a link
 * that opens on an empty review — and if somebody has already emailed it, the
 * client's first impression of TAP is a broken page. The token is the last
 * thing that happens and only happens if all four assets are in.
 *
 * ── Why the token is NOT minted by the edge function ───────────────────────
 *
 * The review-portal function runs with verify_jwt = false, because clients
 * open it without an account. Putting token creation behind it would mean
 * anybody on the internet could mint a review link. create_review_token is an
 * authenticated RPC that checks is_cg_member() first; the edge function reads
 * and records decisions, and mints nothing.
 *
 * ── Resuming, not restarting ───────────────────────────────────────────────
 *
 * Every write is recorded in the progress object the caller holds. A Drive
 * outage halfway through leaves a real order with two assets; pressing the
 * button again must send the OTHER two, not a second order and not a second
 * copy of the first two. There is no DELETE grant on cg_orders for this path
 * and order_assets_guard_delete blocks removing assets outright, so a
 * restart-from-scratch would be permanent litter.
 */

const CLIENT_PROOF_FOLDER = 'TAP Client Proofs';

export interface ClientLinkProgress {
  /** Non-null once the order row exists. */
  orderId: string | null;
  /** Components whose v1 asset is written. */
  uploaded: string[];
  /** Non-null once the link exists. Terminal. */
  token: string | null;
}

export function emptyProgress(): ClientLinkProgress {
  return { orderId: null, uploaded: [], token: null };
}

export interface ProofFailure {
  component: string;
  message: string;
}

export interface ClientLinkOutcome {
  progress: ClientLinkProgress;
  /** The link to send, or null if anything is still outstanding. */
  url: string | null;
  failures: ProofFailure[];
}

/** What the service needs from the outside, so tests can supply their own. */
export interface ClientLinkDeps {
  client: SupabaseClient;
  actorId: string;
  /** Where the app is served from, for building the link. */
  origin: string;
  /**
   * Overridden in tests; the default goes to Drive.
   *
   * Takes the whole batch rather than one file, because resolving the Drive
   * folder is a per-BATCH concern and uploading is a per-file one. A per-file
   * seam forced the caller to choose between four sequential uploads and four
   * racing folder lookups, and the sequential option is the one that shipped.
   */
  uploadProofs?: (files: File[]) => Promise<{ url: string; driveFileId: string }[]>;
  /**
   * The workspace sample SLA. Passed in rather than read here so this stays a
   * pure service over its deps — and because it was previously a bare 7,
   * a third number disagreeing with both the column default and the new-order
   * form, with nothing saying why.
   */
  sampleDueDays?: number;
}

/**
 * Every proof, uploaded at once.
 *
 * ── Why the folder is resolved before the loop and not inside it ───────────
 *
 * ensureProjectFolder searches Drive for a folder by name and creates it if
 * it is missing. Four of those running together each find nothing and each
 * create one, and the proofs end up scattered across four folders all called
 * TAP Client Proofs. That race is why the uploads were sequential.
 *
 * Hoisting the lookup removes the reason rather than working around it: one
 * folder lookup, then four uploads that share the answer and no longer care
 * about each other. Four round trips become one.
 */
async function uploadProofsToDrive(
  client: SupabaseClient,
  files: File[],
): Promise<{ url: string; driveFileId: string }[]> {
  if (files.length === 0) {
    return [];
  }
  return withDriveToken(client, async (token) => {
    const folderId = await ensureProjectFolder(token, 'root', CLIENT_PROOF_FOLDER);
    return Promise.all(
      files.map(async (file) => {
        const created = await uploadFileToDrive(token, folderId, file);
        await shareFileWithLink(token, created.id);
        return {
          url: created.webViewLink ?? `https://drive.google.com/file/d/${created.id}/view`,
          driveFileId: created.id,
        };
      }),
    );
  });
}

function messageOf(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

/**
 * Run — or resume — the three writes.
 *
 * Never throws for a proof that fails: those come back in `failures` so the
 * dialog can name the slot and offer a retry. It does throw if the order
 * itself cannot be written, because nothing has happened yet at that point and
 * there is nothing to resume.
 */
export async function createClientLink(
  deps: ClientLinkDeps,
  draft: ClientLinkDraft,
  progress: ClientLinkProgress,
  clientName: string,
  components: readonly string[],
): Promise<ClientLinkOutcome> {
  if (progress.token !== null) {
    return { progress, url: reviewUrl(deps.origin, progress.token), failures: [] };
  }

  const upload = deps.uploadProofs ?? ((files: File[]) => uploadProofsToDrive(deps.client, files));
  const state: ClientLinkProgress = { ...progress, uploaded: [...progress.uploaded] };

  // ── 1. The proofs, all at once, BEFORE anything is written ───────────────
  //
  // The order used to be created first, which meant a Drive failure left a
  // real order with no proofs and no link — recoverable only by resuming this
  // exact wizard. Uploading first inverts that: a failure here has written
  // nothing anywhere, and the person can fix the file and press the button
  // again with no state to reconcile.
  const pending = orderedProofs(draft, components);
  let stored: { url: string; driveFileId: string }[];
  try {
    stored = await upload(pending.map((proof) => proof.file));
  } catch (cause) {
    // Promise.all rejects on the first failure and does not say which file it
    // was, so the whole batch is reported rather than a slot picked at random.
    return {
      progress: state,
      url: null,
      failures: [{ component: 'Design proofs', message: messageOf(cause) }],
    };
  }
  if (stored.length !== pending.length) {
    return {
      progress: state,
      url: null,
      failures: [
        { component: 'Design proofs', message: 'upload_returned_the_wrong_number_of_files' },
      ],
    };
  }

  // ── 2. Everything else, in ONE transaction ───────────────────────────────
  //
  // create_client_link_order writes the order, its items, the activity row,
  // the proof rows and the token together. Nothing is half-made: the old
  // sequence could stop between any two of eight calls, and the state it left
  // behind is what `progress` existed to resume.
  const { data, error } = await deps.client.rpc('create_client_link_order', {
    p_order_no: draft.orderNo.trim(),
    p_client: clientName,
    p_company_id: draft.companyId,
    p_lead_id: draft.leadId,
    p_sample_due_days: deps.sampleDueDays ?? 21,
    p_items: [
      {
        product_type: draft.productType,
        description: draft.description.trim() === '' ? draft.productType : draft.description,
        units: Number(draft.units),
        unit_price_usd: Number(draft.unitPriceUsd),
        sku: '',
        spec: '',
      },
    ],
    p_assets: pending.map((proof, index) => ({
      component_type: proof.component,
      url: stored[index]?.url ?? '',
      drive_file_id: stored[index]?.driveFileId ?? '',
    })),
    p_token_days: Number(draft.expiryDays),
  });

  if (error) {
    return {
      progress: state,
      url: null,
      failures: [{ component: 'Order', message: error.message }],
    };
  }

  const result = data as { order_id?: string; token?: string } | null;
  const token = result?.token ?? null;
  const orderId = result?.order_id ?? null;
  if (token === null || orderId === null) {
    return {
      progress: state,
      url: null,
      failures: [{ component: 'Review link', message: 'token_not_returned' }],
    };
  }

  return {
    progress: {
      orderId,
      token,
      uploaded: pending.map((proof) => proof.component),
    },
    url: reviewUrl(deps.origin, token),
    failures: [],
  };
}

/**
 * Upload one replacement proof into a slot the client sent back.
 *
 * The revision loop's missing half. upload_order_asset has existed since the
 * revision loop was built and had exactly one caller — the New Client Link
 * wizard, which always creates a NEW order. So a client asking for a change
 * produced a state the interface could not answer: the order could not reach
 * sign-off, and everything downstream of sign-off was unreachable with it.
 *
 * Deliberately the same Drive path as the wizard — same folder, same
 * link-sharing step. A proof that lands somewhere else, or that is not shared,
 * shows the client a broken image rather than the revision they asked for.
 *
 * Order of operations matters: Drive first, database second. If the upload
 * fails there is no half-made version row, and the slot keeps showing the
 * rejected proof and its note, which is the honest state to be left in.
 */
export async function uploadRevisedProof(
  client: SupabaseClient,
  input: { orderId: string; component: string; file: File },
): Promise<void> {
  const uploaded = await withDriveToken(client, async (token) => {
    const folderId = await ensureProjectFolder(token, 'root', CLIENT_PROOF_FOLDER);
    const created = await uploadFileToDrive(token, folderId, input.file);
    await shareFileWithLink(token, created.id);
    return {
      url: created.webViewLink ?? `https://drive.google.com/file/d/${created.id}/view`,
      driveFileId: created.id,
    };
  });

  const { error } = await client.rpc('upload_order_asset', {
    p_order: input.orderId,
    p_component: input.component,
    p_url: uploaded.url,
    p_drive_file_id: uploaded.driveFileId,
  });
  if (error) {
    throw new Error(error.message);
  }
}
